/**
 * Canvas Storage Service
 * Handles local and remote storage of canvas workflows
 */

import { 
  SerializedCanvas, 
  ExportFormat, 
  ImportExportOptions,
  ValidationResult 
} from '../types/canvasSerialization';
import { canvasSerializationService } from './CanvasSerializationService';

/**
 * Storage Backend Types
 */
export type StorageBackend = 'localStorage' | 'indexedDB' | 'remote' | 'file';

/**
 * Storage Options
 */
export interface StorageOptions {
  backend: StorageBackend;
  compression?: boolean;
  encryption?: boolean;
  backup?: boolean;
  versioning?: boolean;
}

/**
 * Canvas Storage Entry
 */
export interface CanvasStorageEntry {
  id: string;
  name: string;
  description?: string;
  thumbnail?: string;
  lastModified: string;
  size: number; // in bytes
  version: string;
  tags: string[];
  isTemplate: boolean;
  metadata: CanvasStorageMetadata;
}

/**
 * Canvas Storage Metadata
 */
export interface CanvasStorageMetadata {
  author?: string;
  createdAt: string;
  updatedAt: string;
  accessCount: number;
  lastAccessed: string;
  storageBackend: StorageBackend;
  compressionRatio?: number;
  checksum: string;
}

/**
 * Storage Result
 */
export interface StorageResult {
  success: boolean;
  id?: string;
  error?: string;
  validationResults?: ValidationResult[];
  metadata?: CanvasStorageMetadata;
}

/**
 * Canvas Storage Service
 */
export class CanvasStorageService {
  private static readonly STORAGE_KEY_PREFIX = 'sengilabs-canvas-';
  private static readonly INDEX_KEY = 'sengilabs-canvas-index';
  private static readonly MAX_LOCAL_STORAGE_SIZE = 5 * 1024 * 1024; // 5MB

  /**
   * Save canvas to storage
   */
  async saveCanvas(
    serializedCanvas: SerializedCanvas,
    options: StorageOptions = { backend: 'localStorage' }
  ): Promise<StorageResult> {
    try {
      const storageKey = this.generateStorageKey(serializedCanvas.id);
      const serializedData = this.prepareForStorage(serializedCanvas, options);
      const metadata = this.generateStorageMetadata(serializedCanvas, serializedData, options);

      switch (options.backend) {
        case 'localStorage':
          return await this.saveToLocalStorage(storageKey, serializedData, metadata);
        
        case 'indexedDB':
          return await this.saveToIndexedDB(storageKey, serializedData, metadata);
        
        case 'file':
          return await this.saveToFile(serializedCanvas.name, serializedData, metadata);
        
        case 'remote':
          return await this.saveToRemote(serializedCanvas, metadata);
        
        default:
          throw new Error(`Unsupported storage backend: ${options.backend}`);
      }
    } catch (error) {
      return {
        success: false,
        error: `Failed to save canvas: ${error}`
      };
    }
  }

  /**
   * Load canvas from storage
   */
  async loadCanvas(
    canvasId: string,
    options: StorageOptions = { backend: 'localStorage' }
  ): Promise<{
    success: boolean;
    canvas?: SerializedCanvas;
    error?: string;
    validationResults?: ValidationResult[];
  }> {
    try {
      const storageKey = this.generateStorageKey(canvasId);

      let data: string;
      switch (options.backend) {
        case 'localStorage':
          data = await this.loadFromLocalStorage(storageKey);
          break;
        
        case 'indexedDB':
          data = await this.loadFromIndexedDB(storageKey);
          break;
        
        case 'remote':
          data = await this.loadFromRemote(canvasId);
          break;
        
        default:
          throw new Error(`Unsupported storage backend: ${options.backend}`);
      }

      const canvas = this.parseStoredData(data, options);
      
      // Update access metadata
      await this.updateAccessMetadata(canvasId, options.backend);

      return {
        success: true,
        canvas,
        validationResults: []
      };
    } catch (error) {
      return {
        success: false,
        error: `Failed to load canvas: ${error}`
      };
    }
  }

  /**
   * List all stored canvases
   */
  async listCanvases(
    backend: StorageBackend = 'localStorage'
  ): Promise<CanvasStorageEntry[]> {
    try {
      switch (backend) {
        case 'localStorage':
          return this.listFromLocalStorage();
        
        case 'indexedDB':
          return this.listFromIndexedDB();
        
        case 'remote':
          return this.listFromRemote();
        
        default:
          return [];
      }
    } catch (error) {
      console.error('Failed to list canvases:', error);
      return [];
    }
  }

  /**
   * Delete canvas from storage
   */
  async deleteCanvas(
    canvasId: string,
    backend: StorageBackend = 'localStorage'
  ): Promise<StorageResult> {
    try {
      const storageKey = this.generateStorageKey(canvasId);

      switch (backend) {
        case 'localStorage':
          return this.deleteFromLocalStorage(storageKey, canvasId);
        
        case 'indexedDB':
          return this.deleteFromIndexedDB(storageKey, canvasId);
        
        case 'remote':
          return this.deleteFromRemote(canvasId);
        
        default:
          throw new Error(`Unsupported storage backend: ${backend}`);
      }
    } catch (error) {
      return {
        success: false,
        error: `Failed to delete canvas: ${error}`
      };
    }
  }

  /**
   * Export canvas to file
   */
  async exportCanvasToFile(
    canvas: SerializedCanvas,
    format: ExportFormat = ExportFormat.JSON,
    options?: Partial<ImportExportOptions>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const exportData = canvasSerializationService.exportCanvas(canvas, format, options);
      const fileName = `${canvas.name.replace(/[^a-z0-9]/gi, '_')}.${format}`;

      // Create and trigger download
      const blob = new Blob([exportData], {
        type: this.getMimeType(format)
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // eslint-disable-next-line no-restricted-syntax -- Export von Canvas-Projektdaten (JSON/YAML/XML), kein Media-Download, Art. 50 KI-VO nicht einschlaegig
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: `Export failed: ${error}`
      };
    }
  }

  /**
   * Import canvas from file
   */
  async importCanvasFromFile(
    file: File,
    options?: Partial<ImportExportOptions>
  ): Promise<{
    success: boolean;
    canvas?: SerializedCanvas;
    validationResults?: ValidationResult[];
    error?: string;
  }> {
    try {
      const content = await this.readFileContent(file);
      const canvas = this.parseImportedContent(content, file.name, options);
      
      // Validate imported canvas
      const result = canvasSerializationService.deserializeCanvas(canvas, options);
      
      return {
        success: true,
        canvas,
        validationResults: result.validationResults
      };
    } catch (error) {
      return {
        success: false,
        error: `Import failed: ${error}`
      };
    }
  }

  // Private methods

  private generateStorageKey(canvasId: string): string {
    return `${CanvasStorageService.STORAGE_KEY_PREFIX}${canvasId}`;
  }

  private prepareForStorage(
    canvas: SerializedCanvas, 
    options: StorageOptions
  ): string {
    let data = JSON.stringify(canvas);
    
    if (options.compression) {
      // Simple compression using base64 (in real implementation, use proper compression)
      data = btoa(data);
    }
    
    if (options.encryption) {
      // Placeholder for encryption
      console.warn('Encryption not implemented in this demo');
    }
    
    return data;
  }

  private generateStorageMetadata(
    canvas: SerializedCanvas, 
    serializedData: string, 
    options: StorageOptions
  ): CanvasStorageMetadata {
    const now = new Date().toISOString();
    
    return {
      author: canvas.metadata.author,
      createdAt: canvas.metadata.createdAt,
      updatedAt: now,
      accessCount: 0,
      lastAccessed: now,
      storageBackend: options.backend,
      compressionRatio: options.compression ? serializedData.length / JSON.stringify(canvas).length : 1,
      checksum: this.generateChecksum(serializedData)
    };
  }

  private generateChecksum(data: string): string {
    // Simple checksum - in production, use a proper hash function
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(16);
  }

  // localStorage implementation
  private async saveToLocalStorage(
    key: string, 
    data: string, 
    metadata: CanvasStorageMetadata
  ): Promise<StorageResult> {
    try {
      // Check storage quota
      if (data.length > CanvasStorageService.MAX_LOCAL_STORAGE_SIZE) {
        throw new Error('Canvas too large for localStorage');
      }

      localStorage.setItem(key, data);
      
      // Update index
      await this.updateLocalStorageIndex(key, metadata);
      
      return {
        success: true,
        id: key,
        metadata
      };
    } catch (error) {
      return {
        success: false,
        error: `localStorage save failed: ${error}`
      };
    }
  }

  private async loadFromLocalStorage(key: string): Promise<string> {
    const data = localStorage.getItem(key);
    if (!data) {
      throw new Error(`Canvas ${key} not found in localStorage`);
    }
    return data;
  }

  private async listFromLocalStorage(): Promise<CanvasStorageEntry[]> {
    try {
      const indexData = localStorage.getItem(CanvasStorageService.INDEX_KEY);
      if (!indexData) {
        return [];
      }
      
      const index: Record<string, CanvasStorageEntry> = JSON.parse(indexData);
      return Object.values(index);
    } catch (error) {
      console.error('Failed to load localStorage index:', error);
      return [];
    }
  }

  private deleteFromLocalStorage(key: string, canvasId: string): StorageResult {
    try {
      localStorage.removeItem(key);
      
      // Update index
      const indexData = localStorage.getItem(CanvasStorageService.INDEX_KEY);
      if (indexData) {
        const index = JSON.parse(indexData);
        delete index[canvasId];
        localStorage.setItem(CanvasStorageService.INDEX_KEY, JSON.stringify(index));
      }
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: `localStorage delete failed: ${error}`
      };
    }
  }

  private async updateLocalStorageIndex(
    key: string, 
    metadata: CanvasStorageMetadata
  ): Promise<void> {
    try {
      const indexData = localStorage.getItem(CanvasStorageService.INDEX_KEY) || '{}';
      const index = JSON.parse(indexData);
      
      // Extract canvas ID from key
      const canvasId = key.replace(CanvasStorageService.STORAGE_KEY_PREFIX, '');
      
      // Load canvas to get basic info
      const canvasData = localStorage.getItem(key);
      if (canvasData) {
        const canvas: SerializedCanvas = JSON.parse(canvasData);
        
        index[canvasId] = {
          id: canvasId,
          name: canvas.name,
          description: canvas.description,
          thumbnail: canvas.metadata.thumbnail,
          lastModified: metadata.updatedAt,
          size: canvasData.length,
          version: canvas.version,
          tags: canvas.metadata.tags,
          isTemplate: false,
          metadata
        };
      }
      
      localStorage.setItem(CanvasStorageService.INDEX_KEY, JSON.stringify(index));
    } catch (error) {
      console.error('Failed to update localStorage index:', error);
    }
  }

  // IndexedDB implementation (simplified)
  private async saveToIndexedDB(
    key: string, 
    data: string, 
    metadata: CanvasStorageMetadata
  ): Promise<StorageResult> {
    // IndexedDB implementation would go here
    throw new Error('IndexedDB storage not implemented in this demo');
  }

  private async loadFromIndexedDB(key: string): Promise<string> {
    throw new Error('IndexedDB storage not implemented in this demo');
  }

  private async listFromIndexedDB(): Promise<CanvasStorageEntry[]> {
    throw new Error('IndexedDB storage not implemented in this demo');
  }

  private deleteFromIndexedDB(key: string, canvasId: string): StorageResult {
    throw new Error('IndexedDB storage not implemented in this demo');
  }

  // Remote storage implementation (simplified)
  private async saveToRemote(
    canvas: SerializedCanvas, 
    metadata: CanvasStorageMetadata
  ): Promise<StorageResult> {
    throw new Error('Remote storage not implemented in this demo');
  }

  private async loadFromRemote(canvasId: string): Promise<string> {
    throw new Error('Remote storage not implemented in this demo');
  }

  private async listFromRemote(): Promise<CanvasStorageEntry[]> {
    throw new Error('Remote storage not implemented in this demo');
  }

  private deleteFromRemote(canvasId: string): Promise<StorageResult> {
    throw new Error('Remote storage not implemented in this demo');
  }

  // File operations
  private async saveToFile(
    name: string, 
    data: string, 
    metadata: CanvasStorageMetadata
  ): Promise<StorageResult> {
    try {
      const fileName = `${name.replace(/[^a-z0-9]/gi, '_')}.json`;
      const blob = new Blob([data], { type: 'application/json' });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // eslint-disable-next-line no-restricted-syntax -- JSON-Datenexport, kein Media-Download, Art. 50 KI-VO nicht einschlaegig
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      return {
        success: true,
        metadata
      };
    } catch (error) {
      return {
        success: false,
        error: `File save failed: ${error}`
      };
    }
  }

  private async readFileContent(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          resolve(e.target.result as string);
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      reader.onerror = () => reject(new Error('File read error'));
      reader.readAsText(file);
    });
  }

  private parseStoredData(data: string, options: StorageOptions): SerializedCanvas {
    let parsedData = data;
    
    if (options.compression) {
      parsedData = atob(data);
    }
    
    return JSON.parse(parsedData);
  }

  private parseImportedContent(
    content: string, 
    fileName: string, 
    options?: Partial<ImportExportOptions>
  ): SerializedCanvas {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'json':
        return JSON.parse(content);
      
      case 'yaml':
      case 'yml':
        throw new Error('YAML import not implemented');
      
      case 'xml':
        throw new Error('XML import not implemented');
      
      default:
        // Try to parse as JSON
        return JSON.parse(content);
    }
  }

  private getMimeType(format: ExportFormat): string {
    switch (format) {
      case ExportFormat.JSON:
        return 'application/json';
      case ExportFormat.YAML:
        return 'text/yaml';
      case ExportFormat.XML:
        return 'application/xml';
      case ExportFormat.BINARY:
        return 'application/octet-stream';
      default:
        return 'application/json';
    }
  }

  private async updateAccessMetadata(canvasId: string, backend: StorageBackend): Promise<void> {
    // Update access count and last accessed timestamp
    if (backend === 'localStorage') {
      try {
        const indexData = localStorage.getItem(CanvasStorageService.INDEX_KEY);
        if (indexData) {
          const index = JSON.parse(indexData);
          if (index[canvasId]) {
            index[canvasId].metadata.accessCount++;
            index[canvasId].metadata.lastAccessed = new Date().toISOString();
            localStorage.setItem(CanvasStorageService.INDEX_KEY, JSON.stringify(index));
          }
        }
      } catch (error) {
        console.error('Failed to update access metadata:', error);
      }
    }
  }
}

// Singleton instance
export const canvasStorageService = new CanvasStorageService();